import axios from "axios";

const platformApi = axios.create({
  baseURL: process.env.REACT_APP_BACKEND_URL || "",
  headers: { "Content-Type": "application/json" },
});

platformApi.interceptors.request.use((config) => {
  const token = localStorage.getItem("platform_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

platformApi.interceptors.response.use((r) => r, (err) => {
  if (err.response?.status === 401) {
    // Only clear platform token and redirect for platform API failures
    const url = err.config?.url || "";
    if (url.includes("/api/platform/")) {
      localStorage.removeItem("platform_token");
      if (window.location.pathname.startsWith("/platform") && window.location.pathname !== "/platform/login") {
        window.location.href = "/platform/login";
      }
    }
  }
  return Promise.reject(err);
});

// Auth
export const platformLogin = (email, password) => platformApi.post("/api/platform/auth/login", { email, password });
export const platformMe = () => platformApi.get("/api/platform/auth/me");
export const platformBootstrap = () => platformApi.post("/api/platform/bootstrap");

// Google SSO — verify a Google credential (ID or access token) → platform JWT + user profile
export const platformGoogleConfig = () => platformApi.get("/api/platform/auth/google/config");
export const platformGoogleLogin = (credential) =>
  platformApi.post("/api/platform/auth/google", { credential });

// Team (RBAC) management
export const platformListUsers = () => platformApi.get("/api/platform/users");
export const platformInviteUser = (payload) => platformApi.post("/api/platform/users/invite", payload);
export const platformUpdateUser = (id, payload) => platformApi.patch(`/api/platform/users/${id}`, payload);
export const platformDeleteUser = (id) => platformApi.delete(`/api/platform/users/${id}`);
export const platformResendInvite = (id) => platformApi.post(`/api/platform/users/${id}/resend-invite`);
export const platformListSessions = (id) => platformApi.get(`/api/platform/users/${id}/sessions`);
export const platformRevokeSessions = (id) => platformApi.delete(`/api/platform/users/${id}/sessions`);
export const platformUserMe = () => platformApi.get("/api/platform/users/me");

// Tenants
export const getTenants = () => platformApi.get("/api/platform/tenants");
export const searchTenants = (q, limit = 10) =>
  platformApi.get("/api/platform/tenants/search", { params: { q, limit } });
export const getTenantsEnriched = () => platformApi.get("/api/platform/tenants-enriched");
export const createTenant = (data) => platformApi.post("/api/platform/tenants", data);
export const getTenantDetail = (id) => platformApi.get(`/api/platform/tenants/${id}/detail`);
export const sendTenantNotification = (id, data) => platformApi.post(`/api/platform/tenants/${id}/notify`, data);
export const updateTenantStatus = (id, status) => platformApi.patch(`/api/platform/tenants/${id}/status`, { status });
export const updateTenantPlan = (id, plan_id) => platformApi.patch(`/api/platform/tenants/${id}/plan`, { plan_id });
export const extendTenantTrial = (id, days = 7) => platformApi.patch(`/api/platform/tenants/${id}/extend-trial`, { days });
export const verifyTenantUserEmail = (tenantId, userId) => platformApi.post(`/api/platform/tenants/${tenantId}/users/${userId}/verify-email`);
export const getLifecycle = () => platformApi.get(`/api/platform/lifecycle`);
export const updateLifecyclePolicy = (patch) => platformApi.put(`/api/platform/lifecycle/settings`, patch);
export const runLifecycle = (dryRun) => platformApi.post(`/api/platform/lifecycle/run`, { dry_run: dryRun });
export const getTenantEntitlementHolds = (tenantId) => platformApi.get(`/api/platform/tenants/${tenantId}/entitlement-holds`);
export const releaseTenantEntitlementHold = (tenantId, holdId) => platformApi.post(`/api/platform/tenants/${tenantId}/entitlement-holds/${holdId}/release`);
export const reconcileTenantEntitlements = (tenantId) => platformApi.post(`/api/platform/tenants/${tenantId}/entitlement-reconcile`);
export const setTenantProtected = (tenantId, protectedFlag) => platformApi.patch(`/api/platform/tenants/${tenantId}/protect`, { protected: protectedFlag });
export const restorePurgedTenant = (snapshotId) => platformApi.post(`/api/platform/lifecycle/snapshots/${snapshotId}/restore`);
export const sendCleanupDigestNow = () => platformApi.post(`/api/platform/lifecycle/digest/send`);
export const getCleanupDigestPreview = () => platformApi.get(`/api/platform/lifecycle/digest`);

// Tenant Notes & Flags
export const getTenantNotes = (id) => platformApi.get(`/api/platform/tenants/${id}/notes`);
export const updateTenantFlags = (id, flags) => platformApi.put(`/api/platform/tenants/${id}/flags`, { flags });
export const addTenantNote = (id, text) => platformApi.post(`/api/platform/tenants/${id}/notes`, { text });
export const deleteTenantNote = (id, noteId) => platformApi.delete(`/api/platform/tenants/${id}/notes/${noteId}`);

// Theme CRUD
export const createPlatformTheme = (data) => platformApi.post("/api/platform/themes", data);
export const updatePlatformTheme = (id, data) => platformApi.patch(`/api/platform/themes/${id}`, data);
export const deletePlatformTheme = (id) => platformApi.delete(`/api/platform/themes/${id}`);
export const getThemeUsage = (id) => platformApi.get(`/api/platform/themes/${id}/usage`);

// Editable Marketing Content
export const getPlatformContent = () => platformApi.get("/api/platform/content");
export const updatePlatformContent = (section, key, value) => platformApi.put(`/api/platform/content/${section}/${key}`, { value });
export const deletePlatformContent = (section, key) => platformApi.delete(`/api/platform/content/${section}/${key}`);

// Plans
export const getPlans = (includeInactive = false) =>
  platformApi.get(`/api/platform/plans?include_inactive=${includeInactive}`);
export const createPlan = (data) => platformApi.post("/api/platform/plans", data);
export const updatePlan = (id, data) => platformApi.patch(`/api/platform/plans/${id}`, data);
export const deletePlan = (id) => platformApi.delete(`/api/platform/plans/${id}`);

export const platformSearch = (q, limit = 6) => platformApi.get(`/api/platform/search`, { params: { q, limit } });

// Payments & Connect oversight
export const platformPaymentsOverview = (days = 30) => platformApi.get(`/api/platform/payments/overview?days=${days}`);
export const platformDisputes = (limit = 50) => platformApi.get(`/api/platform/payments/disputes?limit=${limit}`);
export const updatePlatformSetting = (key, value) => platformApi.put(`/api/platform/settings/${key}`, { value });

// Themes — proper platform endpoint
export const getPlatformThemes = () => platformApi.get("/api/platform/themes");

// Platform Stats
export const getPlatformStats = () => platformApi.get("/api/platform/stats");
export const getPlatformAnalytics = () => platformApi.get("/api/platform/analytics");
export const getPlatformAlerts = () => platformApi.get("/api/platform/alerts");
export const sendAlertDigest = () => platformApi.post("/api/platform/alerts/digest");
export const getPlatformActivity = (limit = 50) => platformApi.get(`/api/platform/activity?limit=${limit}`);

// Starter Packs
export const getStarterPacks = (includeInactive = false) =>
  platformApi.get(`/api/platform/starter-packs${includeInactive ? "?include_inactive=true" : ""}`);
export const getStarterPack = (id) => platformApi.get(`/api/platform/starter-packs/${id}`);
export const createStarterPack = (data) => platformApi.post("/api/platform/starter-packs", data);
export const updateStarterPack = (id, data) => platformApi.patch(`/api/platform/starter-packs/${id}`, data);
export const deleteStarterPack = (id) => platformApi.delete(`/api/platform/starter-packs/${id}`);

// ── Platform Enhancements ────────────────────────────────────────────────────
export const impersonateTenant = (id, reason) =>
  platformApi.post(`/api/platform/tenants/${id}/impersonate`, reason ? { reason } : {});

/** Open the tenant admin in a new tab with a support-mode token (hash fragment → never reaches server logs). */
export const openSupportSession = (data, path = "/admin") => {
  const base = process.env.REACT_APP_BACKEND_URL?.replace(/\/api$/, "") || "";
  localStorage.setItem("impersonation_active", "1");
  window.open(`${base}${path}#__impersonation_token=${encodeURIComponent(data.access_token)}`, "_blank", "noopener");
  return data.mode === "read" ? "Opened tenant admin in a new tab — read-only support session (60 min)"
    : "Opened tenant admin in a new tab — support session (60 min, every change is audited)";
};
export const bulkTenantAction = (action, tenant_ids, extra = {}) =>
  platformApi.post(`/api/platform/tenants/bulk-action`, { action, tenant_ids, ...extra });
// Tenant clean-out (demo tenants, restorable delete, purge)
export const getDemoTenants = () => platformApi.get("/api/platform/tenants/demo");
export const getDeletedTenants = () => platformApi.get("/api/platform/tenants/deleted");
export const searchTenantsForCleanout = (q) => platformApi.get("/api/platform/tenants/cleanout-search", { params: { q } });
export const softDeleteTenants = (tenant_ids) => platformApi.post("/api/platform/tenants/soft-delete", { tenant_ids });
export const restoreTenants = (tenant_ids) => platformApi.post("/api/platform/tenants/restore", { tenant_ids });
export const purgeTenants = (tenant_ids, confirm_count) => platformApi.post("/api/platform/tenants/purge", { tenant_ids, confirm_count });
export const listPlatformInbox = (params = {}) =>
  platformApi.get(`/api/platform/inbox/inquiries`, { params });
export const updatePlatformInboxStatus = (id, status) =>
  platformApi.patch(`/api/platform/inbox/inquiries/${id}`, { status });
export const listPlatformInboxReplies = (id) =>
  platformApi.get(`/api/platform/inbox/inquiries/${id}/replies`);
export const sendPlatformInboxReply = (id, body) =>
  platformApi.post(`/api/platform/inbox/inquiries/${id}/reply`, { body });
export const getCohortAnalytics = (months = 12) =>
  platformApi.get(`/api/platform/analytics/cohorts`, { params: { months } });
export const getSystemHealth = (refresh = false) => platformApi.get(`/api/platform/system-health`, { params: refresh ? { refresh: true } : {} });
export const getDeployStatus = (refresh = false) => platformApi.get(`/api/platform/deploy-status`, { params: refresh ? { refresh: true } : {} });
export const getTenantHealth = (id) => platformApi.get(`/api/platform/tenants/${id}/health`);
export const getTenantActionLogs = (id, params = {}) =>
  platformApi.get(`/api/platform/tenants/${id}/action-logs`, { params });
export const exportTenantData = (id) =>
  platformApi.post(`/api/platform/tenants/${id}/data-export`);
export const hardDeleteTenant = (id, confirm) =>
  platformApi.delete(`/api/platform/tenants/${id}/hard-delete`, { params: { confirm } });

// Theme starter templates
export const listThemeTemplates = (themeSlug) =>
  platformApi.get(`/api/platform/themes/${themeSlug}/templates`);
export const getTemplateDetail = (themeSlug, templateId) =>
  platformApi.get(`/api/platform/themes/${themeSlug}/templates/${templateId}`);
export const snapshotDemoAsTemplate = (themeSlug, payload) =>
  platformApi.post(`/api/platform/themes/${themeSlug}/snapshot`, payload);
export const setDefaultTemplate = (themeSlug, templateId) =>
  platformApi.post(`/api/platform/themes/${themeSlug}/templates/${templateId}/set-default`);
export const deleteThemeTemplate = (themeSlug, templateId) =>
  platformApi.delete(`/api/platform/themes/${themeSlug}/templates/${templateId}`);

// Custom Domain Approvals (Render API)
export const getDomainApprovalStats = () => platformApi.get("/api/platform/domains/stats");
export const getDomainProvisioningConfig = () => platformApi.get("/api/platform/domains/config");
export const listPlatformDomains = (status) =>
  platformApi.get("/api/platform/domains", { params: status ? { status } : {} });
export const acceptPlatformDomain = (id) =>
  platformApi.post(`/api/platform/domains/${id}/accept`);
export const holdPlatformDomain = (id, note) =>
  platformApi.post(`/api/platform/domains/${id}/hold`, { note: note || null });
export const rejectPlatformDomain = (id, note) =>
  platformApi.post(`/api/platform/domains/${id}/reject`, { note: note || null });
export const refreshPlatformDomainSsl = (id) =>
  platformApi.post(`/api/platform/domains/${id}/refresh-status`);
export const testRenderConnection = () =>
  platformApi.post("/api/platform/domains/test-connection");

export default platformApi;
